import type { TrackAppointmentConfirmationParams } from '@/tracking/hooks/useTrackingEvents/types';
import { AppointmentType, type Center } from '@/types/appointments';
import { type AppointmentsReserveFormData } from 'pages/Appointments/AppointmentsReserve/types';
import { CategoryType, barometerMap, categoryTranslationMap } from 'types/tracking';
import { type Tag } from 'types/wellbeing';
import { normalize } from './common';

export const getSpecialityName = (
  specialityId: string,
  translateFunction?: (key: string) => string
): string => {
  if (translateFunction) {
    return translateFunction(`Appointments.speciality.${specialityId}`);
  }
  return specialityId;
};

export const prepareAppointmentConfirmationData = (
  data: AppointmentsReserveFormData,
  centersQuery?: Center[],
  getSpecialityName?: (id: string) => string
): TrackAppointmentConfirmationParams => {
  const baseData = {
    psychologistId: data.psychologist?.id ?? '',
    psychologistName: `${data?.psychologist?.name} ${data?.psychologist?.surname}`,
    speciality: getSpecialityName
      ? getSpecialityName(data.specialityBooked.toString())
      : data.specialityBooked.toString(),
    consultationType: data.contactMethod === AppointmentType.InPerson ? 'in-person' : 'online',
    dateTime: data.dateTime,
    consultationReasons: data.observations || '',
  };

  if (data.contactMethod === AppointmentType.InPerson && data.specialityBooked) {
    return {
      ...baseData,
      centerId: data.backofficeCenterId.toString(),
      centerName:
        centersQuery?.find(center => String(center.id) === String(data.backofficeCenterId))?.name ||
        'Centro no encontrado',
      city: data.city,
    };
  }

  return baseData;
};

export const resolveTrackingCategory = (
  tag: Pick<Tag, 'name' | 'id'> | string | undefined
): CategoryType => {
  if (tag === undefined || tag === '') {
    return CategoryType.OTHER;
  }

  const label = typeof tag === 'string' ? tag : (tag.name ?? tag.id ?? '');
  if (!label) {
    return CategoryType.OTHER;
  }

  const normalizedTag = normalize(label).trim();
  const barometerKey = normalizedTag.replace(/[_\s]+/g, '-');
  const translationKey = normalizedTag.replace(/_/g, ' ');

  return barometerMap[barometerKey] ?? categoryTranslationMap[translationKey] ?? CategoryType.OTHER;
};
